// Anti-cheat configuration and utilities
export const ANTI_CHEAT_CONFIG = {
    // Timing constraints - only flag truly inhuman speeds
    MIN_CLICK_INTERVAL: 2,         // Minimum 2ms between clicks (500 CPS - truly impossible)
    MAX_CLICKS_PER_SECOND: 200,    // Maximum 200 clicks per second (allows legitimate multi-finger/two-handed play)
    MAX_CLICKS_PER_MINUTE: 6000,   // Maximum 6000 clicks per minute (100 CPS average - very high but possible)
    MAX_CLICKS_PER_HOUR: 100000,   // Maximum 100k clicks per hour (allows sustained fast clicking)
    GRACE_PERIOD_SECONDS: 10,      // Don't check CPS limits for first 10 seconds (warm-up period)
    
    // Ban levels (after 3 strikes each)
    BAN_DURATIONS: [
        3600000,        // Level 1: 1 hour (in ms)
        86400000,       // Level 2: 24 hours 
        259200000,      // Level 3: 3 days
        null            // Level 4: Permanent
    ],
    
    // Pattern detection - focus on bot-like patterns, not human speed
    TIMING_HISTORY_SIZE: 100,      // Track last 100 click intervals for better pattern analysis
    VARIANCE_THRESHOLD: 0.005,     // Flag patterns with less than 0.5% variance (only perfect bot patterns)
    IDENTICAL_INTERVAL_THRESHOLD: 0.85, // Flag if 85%+ clicks have identical timing (clear bot behavior)
    MIN_CLICKS_FOR_PATTERN_CHECK: 50, // Need more data for reliable pattern detection
    SEQUENCE_PATTERN_THRESHOLD: 0.75,  // Flag if 75%+ of intervals follow a repeating sequence (definite bot)
    
    // Penalties
    RAPID_CLICK_PENALTY: 3000,     // 3 second cooldown
    PATTERN_PENALTY: 5000,          // 5 second cooldown
    UNTRUSTED_PENALTY: 15000,      // 15 second cooldown
    REPEAT_OFFENDER_MULTIPLIER: 2, // Double penalty for repeat offenders
    
    // Suspicious behavior thresholds
    MAX_SUSPICIOUS_SCORE: 100,     // Ban threshold
    SUSPICIOUS_INCREMENT: {
        UNTRUSTED_CLICK: 25,
        BOT_PATTERN: 15,
        TOO_FAST: 5,
    }
};

export class AntiCheatSystem {
    constructor() {
        this.clickHistory = [];
        this.lastClickTime = 0;
        this.suspiciousScore = 0;
        this.cooldownEndTime = 0;
        this.violationCount = 0;
        this.sessionStartTime = Date.now();
        this.totalSessionClicks = 0;
        this.recentClicks = [];
        this.deviceFingerprint = this.generateFingerprint();
        this.pointerTypes = new Set();  // Track different pointer types used
        this.sequenceDetector = [];  // For detecting repeating sequences
    }

    generateFingerprint() {
        // Enhanced device fingerprinting
        const nav = window.navigator;
        return {
            userAgent: nav.userAgent,
            language: nav.language,
            platform: nav.platform,
            hardwareConcurrency: nav.hardwareConcurrency,
            screenResolution: `${window.screen.width}x${window.screen.height}`,
            colorDepth: window.screen.colorDepth,
            timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
            touchSupport: 'ontouchstart' in window,
            sessionId: Math.random().toString(36).substr(2, 9),
            webGL: this.getWebGLFingerprint(),
            plugins: nav.plugins ? nav.plugins.length : 0,
            doNotTrack: nav.doNotTrack || '0'
        };
    }

    getWebGLFingerprint() {
        try {
            const canvas = document.createElement('canvas');
            const gl = canvas.getContext('webgl') || canvas.getContext('experimental-webgl');
            if (!gl) return 'none';
            const debugInfo = gl.getExtension('WEBGL_debug_renderer_info');
            if (!debugInfo) return 'basic';
            return gl.getParameter(debugInfo.UNMASKED_VENDOR_WEBGL) + '|' + 
                   gl.getParameter(debugInfo.UNMASKED_RENDERER_WEBGL);
        } catch (e) {
            return 'error';
        }
    }

    validateClick(event) {
        const now = Date.now();
        const validationResult = {
            valid: true,
            reason: null,
            penaltyDuration: 0,
            suspiciousIncrement: 0
        };

        // Increment click count first for proper validation
        this.totalSessionClicks++;

        // Check if in cooldown
        if (now < this.cooldownEndTime) {
            this.totalSessionClicks--; // Revert if invalid
            return {
                valid: false,
                reason: 'cooldown',
                remainingTime: this.cooldownEndTime - now
            };
        }

        // 1. Check isTrusted property
        if (!event.isTrusted) {
            this.totalSessionClicks--; // Revert if invalid
            validationResult.valid = false;
            validationResult.reason = 'untrusted_click';
            validationResult.penaltyDuration = ANTI_CHEAT_CONFIG.UNTRUSTED_PENALTY;
            validationResult.suspiciousIncrement = ANTI_CHEAT_CONFIG.SUSPICIOUS_INCREMENT.UNTRUSTED_CLICK;
            return validationResult;
        }

        // 2. Track pointer type (but not position - humans often click same spot)
        if (event.pointerType) {
            this.pointerTypes.add(event.pointerType);
        }

        // 2. Skip individual interval check - rely on pattern detection and rate limits instead
        // This allows legitimate two-handed play (keyboard + mouse) which can have near-simultaneous clicks
        // Bots will still be caught by pattern detection (too regular) and rate limits

        // 3. Check rate limits
        const oneSecondAgo = now - 1000;
        const oneMinuteAgo = now - 60000;
        const oneHourAgo = now - 3600000;

        this.recentClicks = this.recentClicks.filter(time => time > oneHourAgo);
        
        // Add current click to tracking BEFORE checking limits
        this.recentClicks.push(now);
        
        const clicksLastSecond = this.recentClicks.filter(time => time > oneSecondAgo).length;
        const clicksLastMinute = this.recentClicks.filter(time => time > oneMinuteAgo).length;
        const clicksLastHour = this.recentClicks.length;

        // Grace period - don't check CPS limits for first few seconds
        const sessionDuration = (now - this.sessionStartTime) / 1000; // in seconds
        const inGracePeriod = sessionDuration < ANTI_CHEAT_CONFIG.GRACE_PERIOD_SECONDS;

        if (!inGracePeriod && clicksLastSecond > ANTI_CHEAT_CONFIG.MAX_CLICKS_PER_SECOND) {
            this.totalSessionClicks--; // Revert if invalid
            this.recentClicks.pop(); // Remove the click we just added
            validationResult.valid = false;
            validationResult.reason = 'rate_limit_second';
            validationResult.penaltyDuration = ANTI_CHEAT_CONFIG.RAPID_CLICK_PENALTY;
            validationResult.suspiciousIncrement = ANTI_CHEAT_CONFIG.SUSPICIOUS_INCREMENT.TOO_FAST;
            return validationResult;
        }

        if (clicksLastMinute >= ANTI_CHEAT_CONFIG.MAX_CLICKS_PER_MINUTE) {
            this.totalSessionClicks--; // Revert if invalid
            this.recentClicks.pop(); // Remove the click we just added
            validationResult.valid = false;
            validationResult.reason = 'rate_limit_minute';
            validationResult.penaltyDuration = ANTI_CHEAT_CONFIG.RAPID_CLICK_PENALTY * 2;
            validationResult.suspiciousIncrement = ANTI_CHEAT_CONFIG.SUSPICIOUS_INCREMENT.TOO_FAST * 2;
            return validationResult;
        }

        if (clicksLastHour >= ANTI_CHEAT_CONFIG.MAX_CLICKS_PER_HOUR) {
            this.totalSessionClicks--; // Revert if invalid
            this.recentClicks.pop(); // Remove the click we just added
            validationResult.valid = false;
            validationResult.reason = 'rate_limit_hour';
            validationResult.penaltyDuration = ANTI_CHEAT_CONFIG.RAPID_CLICK_PENALTY * 4;
            validationResult.suspiciousIncrement = ANTI_CHEAT_CONFIG.SUSPICIOUS_INCREMENT.TOO_FAST * 3;
            return validationResult;
        }

        // 4. Check click patterns (only after enough clicks)
        const patternValidation = this.analyzeClickPattern(now);
        if (patternValidation.suspicious) {
            this.totalSessionClicks--; // Revert if invalid
            this.recentClicks.pop(); // Remove the click we just added
            validationResult.valid = false;
            validationResult.reason = 'bot_pattern';
            validationResult.penaltyDuration = ANTI_CHEAT_CONFIG.PATTERN_PENALTY;
            validationResult.suspiciousIncrement = ANTI_CHEAT_CONFIG.SUSPICIOUS_INCREMENT.BOT_PATTERN;
            return validationResult;
        }

        // Valid click - update tracking (totalSessionClicks already incremented)
        this.lastClickTime = now;
        this.clickHistory.push(now);
        // recentClicks.push(now) already done before rate limit checks
        
        // Keep history size limited
        if (this.clickHistory.length > ANTI_CHEAT_CONFIG.TIMING_HISTORY_SIZE) {
            this.clickHistory.shift();
        }

        return validationResult;
    }

analyzeClickPattern(currentTime) {
        // Need more clicks before analyzing patterns
        if (this.clickHistory.length < ANTI_CHEAT_CONFIG.MIN_CLICKS_FOR_PATTERN_CHECK) {
            return { suspicious: false };
        }

        // Calculate intervals
        const intervals = [];
        for (let i = 1; i < this.clickHistory.length; i++) {
            intervals.push(this.clickHistory[i] - this.clickHistory[i - 1]);
        }

        // Statistical analysis
        const avgInterval = intervals.reduce((sum, i) => sum + i, 0) / intervals.length;
        const variance = intervals.reduce((sum, i) => sum + Math.pow(i - avgInterval, 2), 0) / intervals.length;
        const stdDev = Math.sqrt(variance);
        const coefficientOfVariation = stdDev / avgInterval;

        // Check for bot-like patterns with more realistic thresholds
        const tooRegular = coefficientOfVariation < ANTI_CHEAT_CONFIG.VARIANCE_THRESHOLD;
        
        // Check for identical intervals (sophisticated bots add small variations)
        const intervalCounts = {};
        intervals.forEach(interval => {
            const rounded = Math.round(interval / 2) * 2; // Round to nearest 2ms for very tight detection
            intervalCounts[rounded] = (intervalCounts[rounded] || 0) + 1;
        });
        
        const maxIdenticalCount = Math.max(...Object.values(intervalCounts));
        const identicalRatio = maxIdenticalCount / intervals.length;
        const tooManyIdentical = identicalRatio > ANTI_CHEAT_CONFIG.IDENTICAL_INTERVAL_THRESHOLD;

        // Check for repeating sequences (e.g., 50ms, 60ms, 55ms, 50ms, 60ms, 55ms...)
        const sequencePattern = this.detectSequencePattern(intervals);
        
        // Check for inhuman patterns - only flag truly impossible patterns
        // Humans can click very fast but will have natural variance
        const superhumanPrecision = avgInterval < 10 && coefficientOfVariation < 0.01; // Only flag if 100+ CPS AND virtually no variance
        
        // Check for suspiciously consistent ultra-fast clicking
        const consistentFastClicking = avgInterval < 5 && coefficientOfVariation < 0.02; // 200+ CPS with perfect timing = bot

        return {
            suspicious: tooRegular || tooManyIdentical || superhumanPrecision || consistentFastClicking || sequencePattern,
            coefficientOfVariation,
            avgInterval,
            identicalRatio,
            details: {
                tooRegular,
                tooManyIdentical,
                superhumanPrecision,
                consistentFastClicking,
                sequencePattern
            }
        };
    }

    applyCooldown(duration, reason) {
        // Apply multiplier for repeat offenders
        if (this.violationCount > 2) {
            duration *= ANTI_CHEAT_CONFIG.REPEAT_OFFENDER_MULTIPLIER;
        }
        
        this.cooldownEndTime = Date.now() + duration;
        this.violationCount++;
        
        return {
            duration,
            reason,
            violationCount: this.violationCount
        };
    }

    updateSuspiciousScore(increment) {
        this.suspiciousScore = Math.min(
            this.suspiciousScore + increment,
            ANTI_CHEAT_CONFIG.MAX_SUSPICIOUS_SCORE
        );
        
        return {
            currentScore: this.suspiciousScore,
            isBanned: this.suspiciousScore >= ANTI_CHEAT_CONFIG.MAX_SUSPICIOUS_SCORE
        };
    }

    getSessionStats() {
        const sessionDuration = Date.now() - this.sessionStartTime;
        const clicksPerMinute = (this.totalSessionClicks / (sessionDuration / 60000)).toFixed(2);
        
        return {
            totalClicks: this.totalSessionClicks,
            sessionDuration,
            clicksPerMinute,
            suspiciousScore: this.suspiciousScore,
            violationCount: this.violationCount,
            deviceFingerprint: this.deviceFingerprint
        };
    }

    detectSequencePattern(intervals) {
        if (intervals.length < 10) return false;
        
        // Check for repeating sequences of 2-5 intervals
        for (let seqLen = 2; seqLen <= 5; seqLen++) {
            if (intervals.length < seqLen * 3) continue; // Need at least 3 repetitions
            
            let matches = 0;
            const baseSeq = intervals.slice(0, seqLen);
            
            for (let i = seqLen; i <= intervals.length - seqLen; i += seqLen) {
                const testSeq = intervals.slice(i, i + seqLen);
                const isMatch = baseSeq.every((val, idx) => 
                    Math.abs(val - testSeq[idx]) <= 5 // Allow only 5ms tolerance for tighter bot detection
                );
                if (isMatch) matches++;
            }
            
            const sequenceRatio = matches / Math.floor(intervals.length / seqLen);
            if (sequenceRatio > ANTI_CHEAT_CONFIG.SEQUENCE_PATTERN_THRESHOLD) {
                return true;
            }
        }
        return false;
    }
    
    reset() {
        this.clickHistory = [];
        this.lastClickTime = 0;
        this.cooldownEndTime = 0;
        this.recentClicks = [];
        this.sequenceDetector = [];
        // Don't reset suspicious score or violation count - track across session
    }
}

// Additional utilities for server-side validation preparation
export function prepareValidationData(antiCheatSystem) {
    return {
        sessionStats: antiCheatSystem.getSessionStats(),
        timestamp: Date.now(),
        fingerprint: antiCheatSystem.deviceFingerprint,
        suspiciousScore: antiCheatSystem.suspiciousScore,
        violationCount: antiCheatSystem.violationCount
    };
}

// Fun and light-hearted warning messages
export const WARNING_MESSAGES = {
    untrusted_click: "🤖 Beep boop! Robots aren't allowed to play! Use your human fingers please!",
    too_fast: "⚡ Whoa there, Flash! Your fingers are moving at superhuman speed!",
    rate_limit_second: "🔥 Your fingers are on fire! Cool down for a bit!",
    rate_limit_minute: "😅 Take a breather! Even Olympic clickers need rest!",
    rate_limit_hour: "🏆 You've reached legendary status! Time for a victory lap (and a break)!",
    bot_pattern: "🎯 Your clicks are suspiciously perfect! Add some human chaos please!",
    cooldown: "❄️ Cooling down... Use this time to stretch those clicking fingers!",
    banned_1hr: "⏰ 1-hour timeout! Take a break and come back with human fingers!",
    banned_24hr: "📅 24-hour ban! See you tomorrow with better clicking habits!",
    banned_3days: "📆 3-day vacation! Time to reflect on fair play!",
    banned_permanent: "🚫 Permanently banned! Contact support if you think this is a mistake."
};